import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import {
  patchGenerarDeclaracion,
  postDeclaracionRetenedorNomina,
  postRechazarDeclaracion,
} from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  mayorIgualCero: [
    {
      validator: async (_, value) => {
        if (value < 0) {
          throw new Error('El valor debe ser mayor o igual a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC válido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP válida.' }],
};

const FormRetenedorNomina = ({
  padron,
  statusDeclaracion,
  visible,
  selectedCargos,
  setVisibleForm,
  isComplementaria,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [INPC, setINPC] = useState(0);

  // watcers
  const impuestoDeterminado = Form.useWatch('impuesto_determinado', form);
  const actualizacion = Form.useWatch('actualizacion', form);
  const recargos = Form.useWatch('recargos', form);
  const multa = Form.useWatch('multa', form);

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const _actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });
      form.setFieldsValue({ actualizacion: round(_actualizacion || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  const calcularRecargos = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const _baseGravada = typeof impuestoDeterminado === 'number'
        ? impuestoDeterminado
        : parseFloat(impuestoDeterminado);

      const _recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: _baseGravada,
        fecha_de_vencimiento: selectedCargos[0]?.fecha_de_vencimiento,
      });

      form.setFieldsValue({ recargos: round(_recargos || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calcularTotalDeContribuciones = () => {
    try {
      const valores = [
        impuestoDeterminado,
        actualizacion,
        recargos,
        multa,
      ].map((valor) => {
        const valorNumerico = typeof valor === 'number' ? valor : parseFloat(valor);
        return Number.isNaN(valorNumerico) ? 0 : valorNumerico;
      });

      const total = valores.reduce((suma, valor) => suma + valor, 0);

      if (total > 0) {
        form.setFieldsValue({ total_de_contribuciones: round(total)[0] });
      }
    } catch (err) {
      _logger(err);
      form.setFieldsValue({ total_de_contribuciones: 0 });
    }
  };
  const calculateINPC = async () => {
    if (!selectedCargos.length) return;

    const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
    const mes = selectedCargos[0]?.identificador_periodo;
    const _inpc = await getINPC(periodo, mes);
    setINPC(_inpc?.[0] ?? {});
  };

  const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleBeforeUnload = () => {
    handleCancel();
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  };

  useEffect(() => {
    calcularActualizacion({ importe: impuestoDeterminado });
  }, [impuestoDeterminado]);

  useEffect(() => {
    calcularRecargos({ importe: actualizacion });
  }, [actualizacion, impuestoDeterminado]);

  useEffect(() => {
    calcularTotalDeContribuciones();
  }, [
    impuestoDeterminado,
    actualizacion,
    recargos,
    multa,
  ]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      calculateINPC();
    }

    window.addEventListener('unload', handleUnload);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada
          && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }

      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
        numero_de_empleados: values.numero_de_empleados,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo: padron?.empresa?.contribuyente?.razon_social || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        numero_de_empleados: values.numero_de_empleados === '' ? 0 : values.numero_de_empleados,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria
          && !isZeroState ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          monto1_de_remuneraciones: 0,
          total1_de_empleados_de_remuneraciones: 0,
          monto2_de_contrato: 0,
          total2_de_empleados_de_contrato: 0,
          valor_total_de_contraprestaciones: 0,
          actualizacion: 0,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_determinado: 0,
          base_de_impuesto: 0,
          monto_pagado_con_anterioridad: 0,
          multa: 0,
          recargos: 0,
          retencion: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionRetenedorNomina(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validations = async (values) => {
    const monto1_de_remuneraciones = values.monto1_de_remuneraciones || 0;
    const total1_de_empleados_de_remuneraciones = values.total1_de_empleados_de_remuneraciones || 0;
    const monto2_de_contrato = values.monto2_de_contrato || 0;
    const total2_de_empleados_de_contrato = values.total2_de_empleados_de_contrato || 0;
    const valor_total_de_contraprestaciones = round(values.valor_total_de_contraprestaciones
      || 0)[0];

    try {
      if (monto1_de_remuneraciones > 0 && total1_de_empleados_de_remuneraciones <= 0) {
        await form.validateFields(['total1_de_empleados_de_remuneraciones']);
        form.setFields([{
          name: 'total1_de_empleados_de_remuneraciones',
          errors: ['Debe ser mayor a cero.'],
        }]);
      } else if (monto1_de_remuneraciones <= 0 && total1_de_empleados_de_remuneraciones > 0) {
        form.setFieldValue('total1_de_empleados_de_remuneraciones', 0);
      }

      if (monto2_de_contrato > 0 && total2_de_empleados_de_contrato < 0) {
        await form.validateFields(['total2_de_empleados_de_contrato']);
        form.setFields([{
          name: 'total2_de_empleados_de_contrato',
          errors: ['Debe ser mayor o igual a cero.'],
        }]);
      } else if (monto2_de_contrato <= 0 && total2_de_empleados_de_contrato > 0) {
        form.setFieldValue('total2_de_empleados_de_contrato', 0);
      }

      if (valor_total_de_contraprestaciones <= 0) {
        await form.validateFields(['valor_total_de_contraprestaciones']);
        form.setFields([{
          name: 'valor_total_de_contraprestaciones',
          errors: ['Debe ser mayor a cero.'],
        }]);
      }
    } catch (error) {
      _logger(error);
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();
    const monto1Remuneraciones = +values.monto1_de_remuneraciones || 0;
    const total1EmpleadosRemuneraciones = +values.total1_de_empleados_de_remuneraciones || 0;
    const monto2Contrato = +values.monto2_de_contrato || 0;
    const total2EmpleadosContrato = +values.total2_de_empleados_de_contrato || 0;

    const valor_total_de_contraprestaciones_total = monto1Remuneraciones + monto2Contrato;

    // IMPUESTOS
    const _impuesto_determinado = Math.floor(valor_total_de_contraprestaciones_total * 0.03) || 0;
    const _actualizacion = values.actualizacion || 0;
    const _recargos = values.recargos || 0;
    const _multa = values.multa || 0;
    const _monto_pagado_con_anterioridad = values.monto_pagado_con_anterioridad || 0;

    const total_de_contribuciones = round([
      _impuesto_determinado,
      _actualizacion,
      _recargos,
      _multa,
    ].reduce((a, b) => a + b, 0))[0];

    const cantidad_a_cargo = total_de_contribuciones - _monto_pagado_con_anterioridad;
    const cantidad_a_cargo_rounded = round(cantidad_a_cargo || 0)[0];

    // Set values
    form.setFieldsValue({
      total_de_contribuciones: round(total_de_contribuciones || 0)[0],
      impuesto_determinado: round(_impuesto_determinado || 0)[0],
      valor_total_de_contraprestaciones: round(valor_total_de_contraprestaciones_total || 0)[0],
    });

    const isNegative = total_de_contribuciones < _monto_pagado_con_anterioridad;

    if (isNegative) {
      const total_a_favor_rounded = round(_monto_pagado_con_anterioridad - total_de_contribuciones);
      form.setFieldsValue({
        cantidad_a_favor: total_a_favor_rounded,
        cantidad_a_cargo: 0,
      });
    } else {
      form.setFieldsValue({
        cantidad_a_cargo: cantidad_a_cargo_rounded,
        cantidad_a_favor: 0,
      });
    }
    validations({
      ...values,
      cantidad_a_cargo: cantidad_a_cargo_rounded,
      monto1_de_remuneraciones: monto1Remuneraciones,
      total1_de_empleados_de_remuneraciones: total1EmpleadosRemuneraciones,
      monto2_de_contrato: monto2Contrato,
      total2_de_empleados_de_contrato: total2EmpleadosContrato,
      valor_total_de_contraprestaciones: round(valor_total_de_contraprestaciones_total || 0)[0],
    });
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              IMPUESTO RETENEDOR NÓMINAS
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>Cancelar</Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>Generar</Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              es_declaracion_en_ceros: false,
              monto1_de_remuneraciones: 0,
              total1_de_empleados_de_remuneraciones: 0,
              monto2_de_contrato: 0,
              total2_de_empleados_de_contrato: 0,
              valor_total_de_contraprestaciones: 0,
              retencion: 0,
              actualizacion: 0,
              recargos: 0,
              multa: 0,
              cantidad_a_cargo: 0,
              cantidad_a_favor: 0,
              total_de_contribuciones: 0,
              impuesto: 0,
              impuesto_determinado: 0,
              monto_pagado_con_anterioridad: isComplementaria ? 0 : 0,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Total de empleados</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            Monto de renumeraciones mensual al personal
                            <br />
                            por parte del tercero
                            <br />
                            (si desconoce el concepto anterior, aplicar la retención con el
                            siguiente concepto).
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="monto1_de_remuneraciones"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric, ..._rules.mayorIgualCero]}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total1_de_empleados_de_remuneraciones"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric, ..._rules.mayorIgualCero]}
                            >
                              <Input onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            Monto mensual conforme al contrato sin IVA
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="monto2_de_contrato"
                              tooltip="Monto mensual conforme al contrato sin IVA"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric, ..._rules.mayorIgualCero]}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total2_de_empleados_de_contrato"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric, ..._rules.mayorIgualCero]}
                            >
                              <Input onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            Valor total de las contraprestaciones
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="valor_total_de_contraprestaciones"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>A. Impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_determinado"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'),
                                ..._rules.numeric,
                                ..._rules.mayorCero,
                              ]}
                            >
                              <Input prefix="$" />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>B. Actualización</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>C. Recargos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>D. Multas</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multa"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>E. Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="(A + B + C + D)"
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>F. Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>G. Monto pagado con anterioridad</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                >
                                  <Input prefix="$" onChange={() => { total(); }} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>H. Cantidad a cargo</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>I. Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormRetenedorNomina.propTypes = {
  padron: propTypes.shape({
    id: propTypes.number,
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  selectedCargos: propTypes.arrayOf(propTypes.shape({
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.string,
    }),
    identificador_periodo: propTypes.string,
    importe: propTypes.number,
    fecha_de_vencimiento: propTypes.string,
  })),
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  declaracionId: propTypes.number.isRequired,
  callbackCancel: propTypes.func.isRequired,
};

FormRetenedorNomina.defaultProps = {
  isComplementaria: false,
  visible: false,
  selectedCargos: [],
};

export default FormRetenedorNomina;
